import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { ConversionTarget, CreateJobRequest, DocumentJob, DocumentOperation, OcrLanguage } from "@xhalo/document-contracts";
import type { SplitMode } from "@xhalo/document-browser-engine";
import { documentApiClient, documentMimeForFile, DocumentApiError } from "./cloud-document-client";
import { localSignInHref } from "./auth-client";
import { formatBytes } from "./format";

export interface CloudJobSettings {
  ranges: string;
  splitMode: SplitMode;
  pagesPerOutput: number;
  format: "png" | "jpeg";
  dpi: number;
  compressionMode: "lossless" | "balanced";
  quality: number;
  ocrMode: "missing_text" | "all";
  ocrLanguages: readonly OcrLanguage[];
  conversionTarget: ConversionTarget;
}

interface CloudExecutionProps {
  operation: DocumentOperation;
  files: readonly File[];
  existingAssetIds?: readonly string[];
  existingAssetMimes?: readonly string[];
  inputBytes: number;
  requiredCount: number;
  settings: CloudJobSettings;
  hideWhenUnavailable?: boolean;
  onResult(result: { id: string; name: string; url: string; createdAt: number }): void;
}

export function createCloudJobRequest(operation: DocumentOperation, inputAssetIds: string[], settings: CloudJobSettings): CreateJobRequest {
  const common = { inputAssetIds, execution: "server" as const, idempotencyKey: crypto.randomUUID() };
  if (operation === "merge") return { ...common, operation, parameters: {} };
  if (operation === "split") {
    if (settings.splitMode === "every") {
      return { ...common, operation, parameters: { mode: "every_n", every: settings.pagesPerOutput } };
    }
    if (settings.splitMode === "selected") {
      return { ...common, operation, parameters: { mode: "selected", pages: settings.ranges || "1" } };
    }
    return { ...common, operation, parameters: { mode: "ranges", ranges: settings.ranges || "1" } };
  }
  if (operation === "compress") return {
    ...common,
    operation,
    parameters: settings.compressionMode === "lossless"
      ? { mode: "lossless" }
      : { mode: "balanced", imageDpi: settings.dpi, quality: settings.quality },
  };
  if (operation === "pdf_to_docx") return { ...common, operation, parameters: { ocrFallback: false } };
  if (operation === "pdf_to_images") return {
    ...common,
    operation,
    parameters: {
      format: settings.format,
      dpi: settings.dpi,
      ...(settings.ranges ? { pages: settings.ranges } : {}),
      ...(settings.format === "jpeg" ? { quality: settings.quality } : {}),
    },
  };
  if (operation === "convert") return { ...common, operation, parameters: { target: settings.conversionTarget } };
  return { ...common, operation: "ocr", parameters: { mode: settings.ocrMode, languages: [...settings.ocrLanguages] } };
}

function isPollingComplete(job?: DocumentJob): boolean {
  return job?.status === "succeeded" || job?.status === "failed" || job?.status === "cancelled" || job?.status === "dead_lettered";
}

function resultName(operation: DocumentOperation, mimeType: string): string {
  const extension = mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? "docx"
    : mimeType === "application/zip" ? "zip"
      : mimeType === "application/epub+zip" ? "epub"
        : mimeType === "application/vnd.amazon.ebook" ? "azw3"
      : mimeType === "image/png" ? "png"
        : mimeType === "image/jpeg" ? "jpg" : "pdf";
  return `xhalo-${operation.replaceAll("_", "-")}.${extension}`;
}

function compressionSizeLabels(language: string): { before: string; after: string } {
  if (language === "zh-TW") return { before: "壓縮前", after: "壓縮後" };
  if (language === "ja") return { before: "圧縮前", after: "圧縮後" };
  if (language === "en") return { before: "Before", after: "After" };
  return { before: "压缩前", after: "压缩后" };
}

export function CloudExecution({ operation, files, existingAssetIds = [], existingAssetMimes = [], inputBytes, requiredCount, settings, hideWhenUnavailable = false, onResult }: CloudExecutionProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [submittedInputBytes, setSubmittedInputBytes] = useState(0);
  const completedAssetRef = useRef<string | null>(null);
  const capabilities = useQuery({
    queryKey: ["document-capabilities"],
    queryFn: () => documentApiClient.getCapabilities(),
    staleTime: 60_000,
  });
  const capability = capabilities.data?.find((item) => item.operation === operation);
  const matchingServerPairs = operation === "convert"
    ? capability?.conversionPairs?.filter((pair) => pair.target === settings.conversionTarget && pair.executionModes.includes("server")) ?? []
    : [];
  const serverAvailable = operation === "convert"
    ? matchingServerPairs.length > 0
    : capability?.executionModes.includes("server") === true;
  const selectedMimes = [...files.map((file) => documentMimeForFile(file) ?? ""), ...existingAssetMimes];
  const serverInputsCompatible = operation !== "convert" || (
    selectedMimes.length === 1 && matchingServerPairs.some((pair) => selectedMimes.every((mime) => pair.inputMimeTypes.includes(mime)))
  );

  const job = useQuery({
    queryKey: ["document-job", jobId],
    enabled: Boolean(jobId),
    queryFn: () => documentApiClient.getJob(jobId!),
    refetchInterval: (query) => {
      const current = query.state.data;
      if (isPollingComplete(current)) return false;
      if (typeof document !== "undefined" && document.hidden) return 15_000;
      return Date.now() - startedAt < 30_000 ? 2_000 : 5_000;
    },
  });

  const start = useMutation({
    mutationFn: async () => {
      const uploadedAssetIds = await Promise.all(files.map((file) => operation === "convert"
        ? documentApiClient.uploadDocument(file)
        : documentApiClient.uploadPdf(file)));
      const inputAssetIds = [...existingAssetIds, ...uploadedAssetIds];
      const created = await documentApiClient.createJob(createCloudJobRequest(operation, inputAssetIds, settings));
      return { created, submittedInputBytes: inputBytes };
    },
    onSuccess: ({ created, submittedInputBytes: submittedBytes }) => {
      completedAssetRef.current = null;
      setSubmittedInputBytes(submittedBytes);
      setStartedAt(Date.now());
      setJobId(created.id);
      queryClient.setQueryData(["document-job", created.id], created);
    },
  });

  const cancel = useMutation({
    mutationFn: () => documentApiClient.cancelJob(jobId!),
    onSuccess: (cancelled) => queryClient.setQueryData(["document-job", cancelled.id], cancelled),
  });

  const output = job.data?.status === "succeeded" ? job.data.outputs?.[0] : undefined;
  const download = useQuery({
    queryKey: ["document-download", output?.assetId],
    enabled: Boolean(output?.assetId),
    queryFn: () => documentApiClient.createDownloadUrl(output!.assetId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!output || !download.data || completedAssetRef.current === output.assetId) return;
    completedAssetRef.current = output.assetId;
    onResult({
      id: output.assetId,
      name: resultName(operation, output.mimeType),
      url: download.data,
      createdAt: Date.now(),
    });
  }, [download.data, onResult, operation, output]);

  const requestError = start.error instanceof DocumentApiError ? start.error : job.error instanceof DocumentApiError ? job.error : null;
  const needsSignIn = requestError?.code === "AUTH_REQUIRED";
  const needsBetaAccess = requestError?.code === "BETA_ACCESS_REQUIRED";
  const hasUnhandledError = (start.isError || job.isError) && !requestError;
  const active = job.data && !isPollingComplete(job.data);
  const canStart = serverAvailable && serverInputsCompatible && files.length + existingAssetIds.length >= requiredCount && settings.ocrLanguages.length > 0 && !start.isPending && !active;
  const sizeLabels = compressionSizeLabels(i18n.language);

  if (hideWhenUnavailable && !serverAvailable) return null;

  return <>
    <p className="privacy-note cloud-note"><span aria-hidden="true">☁</span>{t("workspace.serverNote")}</p>
    {capabilities.isPending ? <p className="status-message" role="status">{t("workspace.serverChecking")}</p> : null}
    {!capabilities.isPending && !serverAvailable ? <p className="status-message error" role="status">{t("workspace.serverUnavailable")}</p> : null}
    {serverAvailable ? <button className="primary-button" type="button" disabled={!canStart} onClick={() => start.mutate()}>
      {start.isPending ? t("workspace.uploading") : active ? t("workspace.processing") : t("workspace.runCloud")}
    </button> : null}
    {active ? <div className="cloud-progress" role="status"><span>{t(`workspace.job.${job.data!.status}`)}</span><strong>{job.data!.progress}%</strong><progress max="100" value={job.data!.progress} /><button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending}>{cancel.isPending ? t("workspace.cancelling") : t("workspace.cancel")}</button></div> : null}
    {needsSignIn ? <a className="primary-button sign-in-button" href={localSignInHref(`${location.pathname}${location.search}${location.hash}`, i18n.language)}>{t("workspace.signIn")}</a> : null}
    {needsBetaAccess ? <p className="status-message error" role="alert">{t("workspace.betaRequired")}</p> : null}
    {requestError && !needsSignIn && !needsBetaAccess ? <p className="status-message error" role="alert">{t("workspace.error")} ({requestError.code})</p> : null}
    {hasUnhandledError ? <p className="status-message error" role="alert">{t("workspace.error")}</p> : null}
    {job.data?.status === "failed" || job.data?.status === "dead_lettered" ? <p className="status-message error" role="alert">{t("workspace.error")} ({job.data.errorCode ?? "ENGINE_FAILED"})</p> : null}
    {job.data?.status === "cancelled" ? <p className="status-message" role="status">{t("workspace.cancelled")}</p> : null}
    {download.isError ? <p className="status-message error" role="alert">{t("workspace.downloadError")}</p> : null}
    {output && download.data ? <div className="result-card" role="status"><strong>{t("workspace.success")}</strong>{operation === "compress" ? <dl className="result-size-grid"><div><dt>{sizeLabels.before}</dt><dd>{formatBytes(submittedInputBytes, i18n.language)}</dd></div><div><dt>{sizeLabels.after}</dt><dd>{formatBytes(output.bytes, i18n.language)}</dd></div></dl> : null}<a href={download.data} download={resultName(operation, output.mimeType)}><ArrowDownTrayIcon />{t("workspace.download")}</a></div> : null}
  </>;
}
